import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatPKR, LOGO_URL } from "@/lib/api";
import { Printer, ArrowLeft } from "lucide-react";

function configLine(c) {
  if (!c) return "";
  const parts = [];
  if (c.bedding_type) parts.push(`Type: ${c.bedding_type}`);
  if (c.size) parts.push(`Size: ${c.size}`);
  if (c.pillow_count) parts.push(`Pillows: ${c.pillow_count}`);
  if (c.curtain_size) parts.push(`Size: ${c.curtain_size}`);
  return parts.join(" · ");
}

export default function AdminInvoice() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/orders/${id}`).then((r) => setOrder(r.data)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-10 text-ink-soft">Loading…</div>;
  if (!order) return <div className="p-10 text-ink-soft">Order not found</div>;

  return (
    <div className="bg-cream-warm min-h-screen py-8 print:bg-white print:py-0" data-testid="admin-invoice">
      <div className="max-w-3xl mx-auto px-6">
        <div className="flex justify-between items-center mb-4 print:hidden">
          <Link to="/admin/orders" className="inline-flex items-center gap-2 text-xs text-ink-soft uppercase tracking-widest" data-testid="invoice-back">
            <ArrowLeft size={14} /> Back to Orders
          </Link>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream px-4 py-2 text-xs uppercase tracking-widest transition-colors"
            data-testid="invoice-print"
          >
            <Printer size={14} /> Print Invoice
          </button>
        </div>

        <div className="bg-white border border-border-subtle p-8 sm:p-12 print:border-0">
          <div className="flex justify-between items-start pb-6 border-b border-border-subtle">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="" className="h-14 w-14 object-contain" />
              <div>
                <div className="font-display text-2xl text-ink">Bedding Lounge</div>
                <div className="label-eyebrow !text-[10px]">Soft Luxury · Nature</div>
              </div>
            </div>
            <div className="text-right">
              <div className="label-eyebrow !text-[10px]">Invoice</div>
              <div className="font-display text-2xl text-ink">{order.order_number}</div>
              <div className="text-xs text-ink-soft mt-1">{new Date(order.created_at).toLocaleString("en-PK")}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 py-6 border-b border-border-subtle">
            <div>
              <div className="label-eyebrow !text-[10px] mb-2">Bill To</div>
              <div className="text-ink font-semibold">{order.customer.full_name}</div>
              <div className="text-sm text-ink-soft mt-1">
                {order.customer.email}<br />
                {order.customer.phone}<br />
                {order.customer.address_line1}{order.customer.address_line2 ? `, ${order.customer.address_line2}` : ""}<br />
                {order.customer.city}, {order.customer.province} {order.customer.postal_code}
              </div>
            </div>
            <div>
              <div className="label-eyebrow !text-[10px] mb-2">Payment</div>
              <div className="text-ink font-semibold">Cash on Delivery</div>
              <div className="text-sm text-ink-soft mt-1 uppercase tracking-widest">Status: {order.status}</div>
              {order.customer.notes && (
                <div className="text-xs text-ink-soft mt-3 italic">"{order.customer.notes}"</div>
              )}
            </div>
          </div>

          <table className="w-full mt-6">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-ink-soft border-b border-border-subtle">
                <th className="py-3">Item</th>
                <th className="py-3 text-center">Qty</th>
                <th className="py-3 text-right">Unit Price</th>
                <th className="py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((it, idx) => (
                <tr key={idx} className="border-b border-border-subtle">
                  <td className="py-4">
                    <div className="font-semibold text-ink">{it.name}</div>
                    {configLine(it.config) && <div className="text-xs text-ink-soft mt-0.5">{configLine(it.config)}</div>}
                  </td>
                  <td className="py-4 text-center text-ink">{it.quantity}</td>
                  <td className="py-4 text-right text-ink-soft">{formatPKR(it.price)}</td>
                  <td className="py-4 text-right text-ink font-semibold">{formatPKR(it.price * it.quantity)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-end mt-6">
            <div className="w-full sm:w-72 space-y-2 text-sm">
              <div className="flex justify-between text-ink-soft"><span>Subtotal</span><span>{formatPKR(order.subtotal)}</span></div>
              <div className="flex justify-between text-ink-soft"><span>Shipping</span><span>{formatPKR(order.shipping)}</span></div>
              <div className="flex justify-between font-display text-2xl text-ink pt-2 border-t border-ink/15">
                <span>Total Due</span><span>{formatPKR(order.total)}</span>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-border-subtle text-center">
            <p className="text-xs text-ink-soft tracking-widest uppercase">Thank you for choosing Bedding Lounge</p>
            <p className="text-[10px] text-ink-soft mt-1">Beddinglounge@gmail.com · +92 335 4233258</p>
          </div>
        </div>
      </div>
    </div>
  );
}
